import styles from './Login.module.scss';
import { useNavigate } from 'react-router-dom';
import { Input } from 'shared/components/inputs/Input';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {  signIn,  signOut,  fetchAuthSession,} from 'aws-amplify/auth';
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const schema = z.object({
  email: z
    .string()
    .email('Insira um email válido')
    .min(1, 'O campo e-mail é obrigatório'),

  password: z
    .string()
    .min(1, 'O campo senha é obrigatório'),
});

type FormData = z.infer<typeof schema>;

export const Login = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] =
    useState<string | null>(null);
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] =
    useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  useEffect(() => { signOut().catch(() => {});  }, []);

  const onSubmit = async ( data: FormData  ) => {
    setAuthError(null);
    setPasswordResetDialogOpen(false);
    let cognitoToken = "";

    try {

      /* ================================
         LOGIN COGNITO
      ================================ */

      await signIn({
        username: data.email,
        password: data.password,
      });

      const session =
        await fetchAuthSession();

      cognitoToken =
        session.tokens?.idToken?.toString() ?? "";

      if (!cognitoToken) {

        throw new Error(
          "Token Cognito não encontrado"
        );
      }

      localStorage.setItem(
        "cognito-token",
        cognitoToken
      );
    } catch (error: any) {

      console.error(
        "ERRO AO LOGAR NO COGNITO:",
        error
      );

      setAuthError(
        "E-mail ou senha inválidos."
      );

      return;
    }

    try {

      /* ================================
         LOGIN API
      ================================ */

      const response =
        await axios.post(

          `${process.env.REACT_APP_API_URL}/identity/login`,

          {
            email: data.email,
            password: data.password,
          },

          {
            headers: {
              Authorization:
                `Bearer ${cognitoToken}`,
            },
          }
        );


      localStorage.setItem(
        "apiAccessToken",
        response.data.accessToken
      );

      localStorage.setItem(
        "apiRefreshToken",
        response.data.refreshToken
      );

      window.location.href = "/";

    } catch (error: any) {

      console.error(
        "ERRO AO LOGAR NA API:",
        error
      );

      console.error(
        "STATUS:",
        error?.response?.status
      );

      console.error(
        "BODY:",
        error?.response?.data
      );

      if (
        axios.isAxiosError(error) &&
        error.response?.status === 401
      ) {
        localStorage.removeItem("user");
        localStorage.removeItem("perfilAtivo");
        localStorage.removeItem("userName");
        localStorage.removeItem("apiAccessToken");
        localStorage.removeItem("apiRefreshToken");
        localStorage.removeItem("cognito-token");
        sessionStorage.clear();

        await signOut().catch(() => {});

        setPasswordResetDialogOpen(true);

        return;
      }

      setAuthError(
        error?.response?.data?.message ||
        "E-mail ou senha inválidos."
      );
    }
  };

  return (
    <div className={styles.page}>

      <div
        className={styles.iconClose}
        onClick={() => navigate("/")}
      >
        <X size={16} />
      </div>

      <form className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
        onKeyDown={(e) => {

          if (e.key === 'Enter') {

            e.preventDefault();

            handleSubmit(onSubmit)();
          }
        }}
      >

        <div className={styles.containerLabel}>

          <label className={styles.label}>
            Login
          </label>

          <Input
            placeholder="seu@email.com"
            type="email"
            name="email"
            error={errors.email?.message}
            register={register}
            customClass={styles.customLogin}
          />

        </div>

        <div className={styles.containerLabel}>

          <label className={styles.label}>
            Senha
          </label>

          <Input
            placeholder="Senha"
            type="password-toggle"
            name="password"
            register={register}
            error={errors.password?.message}
            customClass={styles.customInputPW}
          />

        </div>

        {authError && (

          <p className={styles.errorMessage}>
            {authError}
          </p>

        )}

        <div className={styles.containerLink}>

          <a
            href="usuario/recuperar-senha"
            className={styles.btnSenha}
          >
            Esqueceu sua senha?
          </a>

        </div>

        <div className={styles.containerBtn}>

          <button
            className={styles.btnEntrar}
            type="submit"
            disabled={!isValid}
          >
            Entrar
          </button>

        </div>

      </form>

      <div className={styles.separator}>

        <span className={styles.separatorSpan}>
          ou
        </span>

      </div>

      <div className={styles.containerLinkCadastro}>

        <p>Não tem uma conta?</p>

        <a
          href="/usuario/cadastrar"
          className={styles.linkCadastro}
        >
          Cadastre-se
        </a>

      </div>

      {passwordResetDialogOpen && (
        <div className={styles.dialogOverlay}>
          <div
            className={styles.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="password-reset-dialog-title"
          >
            <h3 id="password-reset-dialog-title">
              Redefina sua senha
            </h3>

            <p>
              Ocorreu um erro ao tentar logar. Para
              continuar, redefina sua senha.
            </p>

            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.dialogSecondaryButton}
                onClick={() => setPasswordResetDialogOpen(false)}
              >
                Voltar
              </button>

              <button
                type="button"
                className={styles.dialogPrimaryButton}
                onClick={() => navigate("/usuario/recuperar-senha")}
              >
                Redefinir senha
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
